import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Supported languages — sorted alphabetically by nativeName, with flag emojis
export const SUPPORTED_LANGUAGES = [
  { code: 'ar', name: 'Arabic',      nativeName: 'العربية',        flag: '🇸🇦' },
  { code: 'id', name: 'Indonesian',  nativeName: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'cs', name: 'Czech',       nativeName: 'Čeština',           flag: '🇨🇿' },
  { code: 'da', name: 'Danish',      nativeName: 'Dansk',            flag: '🇩🇰' },
  { code: 'de', name: 'German',      nativeName: 'Deutsch',          flag: '🇩🇪' },
  { code: 'en', name: 'English',     nativeName: 'English',          flag: '🇬🇧' },
  { code: 'es', name: 'Spanish',     nativeName: 'Español',          flag: '🇪🇸' },
  { code: 'fr', name: 'French',      nativeName: 'Français',         flag: '🇫🇷' },
  { code: 'hi', name: 'Hindi',       nativeName: 'हिन्दी',          flag: '🇮🇳' },
  { code: 'it', name: 'Italian',     nativeName: 'Italiano',         flag: '🇮🇹' },
  { code: 'ja', name: 'Japanese',    nativeName: '日本語',           flag: '🇯🇵' },
  { code: 'ko', name: 'Korean',      nativeName: '한국어',           flag: '🇰🇷' },
  { code: 'hu', name: 'Hungarian',   nativeName: 'Magyar',           flag: '🇭🇺' },
  { code: 'nl', name: 'Dutch',       nativeName: 'Nederlands',       flag: '🇳🇱' },
  { code: 'no', name: 'Norwegian',   nativeName: 'Norsk',            flag: '🇳🇴' },
  { code: 'pl', name: 'Polish',      nativeName: 'Polski',           flag: '🇵🇱' },
  { code: 'pt', name: 'Portuguese',  nativeName: 'Português',        flag: '🇧🇷' },
  { code: 'ro', name: 'Romanian',    nativeName: 'Română',          flag: '🇷🇴' },
  { code: 'ru', name: 'Russian',     nativeName: 'Русский',         flag: '🇷🇺' },
  { code: 'fi', name: 'Finnish',     nativeName: 'Suomi',            flag: '🇫🇮' },
  { code: 'sv', name: 'Swedish',     nativeName: 'Svenska',          flag: '🇸🇪' },
  { code: 'th', name: 'Thai',        nativeName: 'ภาษาไทย',       flag: '🇹🇭' },
  { code: 'tr', name: 'Turkish',     nativeName: 'Türkçe',          flag: '🇹🇷' },
  { code: 'uk', name: 'Ukrainian',   nativeName: 'Українська',      flag: '🇺🇦' },
  { code: 'vi', name: 'Vietnamese',  nativeName: 'Tiếng Việt',      flag: '🇻🇳' },
  { code: 'zh', name: 'Chinese',     nativeName: '中文',            flag: '🇨🇳' },
];

export const SUPPORTED_LANG_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

// RTL languages
export const RTL_LANGUAGES = ['ar'];

// Dynamically import translation files
const loadTranslation = async (lang: string) => {
  try {
    const module = await import(`./locales/${lang}.json`);
    return module.default;
  } catch {
    // Fallback to English if translation not found
    const module = await import('./locales/en.json');
    return module.default;
  }
};

// Detect language from URL path (e.g., /fr/ → 'fr')
export function getLangFromPath(pathname: string): string {
  const match = pathname.match(/^\/([a-z]{2})(\/|$)/);
  if (match && SUPPORTED_LANG_CODES.includes(match[1]) && match[1] !== 'en') {
    return match[1];
  }
  return 'en';
}

// Get path without language prefix
export function getPathWithoutLang(pathname: string): string {
  const lang = getLangFromPath(pathname);
  if (lang === 'en') return pathname;
  return pathname.replace(new RegExp(`^/${lang}`), '') || '/';
}

// Get path with language prefix
export function getPathWithLang(pathname: string, lang: string): string {
  const cleanPath = getPathWithoutLang(pathname);
  if (lang === 'en') return cleanPath;
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
}

// Initialize i18n
const initI18n = async () => {
  // Detect language from URL path first, then browser
  const pathLang = getLangFromPath(window.location.pathname);
  
  // Load the detected language
  const translations = await loadTranslation(pathLang);
  
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: pathLang,
      fallbackLng: 'en',
      resources: {
        [pathLang]: { translation: translations },
      },
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['path', 'localStorage', 'navigator'],
        lookupFromPathIndex: 0,
        caches: ['localStorage'],
      },
    });

  // Load English as fallback if not already loaded
  if (pathLang !== 'en') {
    const enTranslations = await loadTranslation('en');
    i18n.addResourceBundle('en', 'translation', enTranslations);
  }

  return i18n;
};

export { initI18n };
export default i18n;
